// Auth Event Types
export type AuthEventType = 
  | 'user-login-attempt'
  | 'user-login-success' 
  | 'user-login-failed'
  | 'user-signup-attempt'
  | 'user-signup-success'
  | 'user-signup-failed'
  | 'user-logout'
  | 'password-reset-requested';

interface AuthEventData {
  email?: string;
  error?: string;
  timestamp: string;
  userAgent?: string;
  source?: 'web' | 'mobile';
}

class AuthEventTracker {
  private events: Array<{ type: AuthEventType; data: AuthEventData }> = [];

  track(eventType: AuthEventType, data: Partial<AuthEventData> = {}) {
    const eventData: AuthEventData = {
      ...data,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      source: 'web'
    };

    this.events.push({ type: eventType, data: eventData });
    
    // Dispatch custom event for real-time tracking
    window.dispatchEvent(new CustomEvent('authEvent', {
      detail: { type: eventType, data: eventData }
    }));

    // Send to Firebase Analytics
    this.sendToFirebase(eventType, eventData);

    console.log(`🔥 Auth Event: ${eventType}`, eventData);
  }

  private async sendToFirebase(eventType: AuthEventType, data: AuthEventData) {
    try {
      // Import Firebase Analytics
      const { getAnalytics, logEvent, isSupported } = await import('firebase/analytics');
      
      // Check if Analytics is supported
      const supported = await isSupported();
      if (!supported) {
        console.warn('Firebase Analytics not supported in this environment');
        return;
      }
      
      const analytics = getAnalytics();
      
      // Enable debug mode for localhost
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        // Enable debug mode
        (window as any).gtag('config', 'G-F81GEFE6V4', {
          debug_mode: true
        });
        console.log('🔧 Firebase Analytics debug mode enabled for localhost');
      }
      
      // Use Firebase predefined events with proper parameters
      switch (eventType) {
        case 'user-login-attempt':
          logEvent(analytics, 'login', {
            method: 'email'
          });
          break;
        case 'user-login-success':
          logEvent(analytics, 'login', {
            method: 'email'
          });
          break;
        case 'user-login-failed':
          logEvent(analytics, 'login', {
            method: 'email'
          });
          break;
        case 'user-signup-attempt':
          logEvent(analytics, 'sign_up', {
            method: 'email'
          });
          break;
        case 'user-signup-success':
        case 'user-signup-failed':
          logEvent(analytics, 'sign_up', {
            method: 'email'
          });
          break;
        case 'user-logout':
          // Use custom event for logout as it's not predefined
          logEvent(analytics, 'user_logout', {
            method: 'manual'
          });
          break;
        case 'password-reset-requested':
          // Use custom event for password reset
          logEvent(analytics, 'password_reset_request', {
            method: 'email'
          });
          break;
      }
      
      console.log(`📊 Firebase Analytics: ${eventType} sent successfully`);
    } catch (error) {
      console.error('Firebase Analytics error:', error);
    }
  }

  getEvents() {
    return this.events;
  }

  clearEvents() {
    this.events = [];
  }
}

export const authEventTracker = new AuthEventTracker();